import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";
import type { TelemetryOutageCauseKey } from "../data/types";
import { useAuthStore } from "./useAuthStore";

/**
 * Chronological log of admin outage notifications (JAA-1062, §6.3 v1.6 of
 * the FE push architecture spec) — what the bell-icon panel (JAA-1096)
 * renders. Deliberately separate from useFleetOutageStore, which holds
 * *current* per-facility outage state: this store is a log of *events*
 * (an outage opening, and later resolving, are two distinct entries), not
 * a snapshot. Only ever populated from live event: outage pushes — the
 * initial GET /admin/telemetry-outages snapshot seeds current state, not a
 * notification history, so it never writes here.
 *
 * UNLIKE the other telemetry stores (useFleetOutageStore,
 * useTelemetryConnectionStore), this one IS persisted — a plain page
 * refresh used to wipe the entire notification log, which admins flagged
 * as surprising for something framed as a history/inbox.
 *
 * Two things this needed to get right, both from the same review:
 * - Per-user storage key (userScopedStorage below), not a single shared
 *   one — two different admins logging into the same browser must never
 *   see each other's notification history. Keyed by the authenticated
 *   user's sub (falling back to email, then "anon" pre-login) — NOT
 *   cleared on logout, since that would just as surprisingly wipe the
 *   SAME admin's history on every routine re-login. Because persist only
 *   rehydrates once at module load, RequireAuth.tsx and SignInForm.tsx
 *   both call `useOutageNotificationStore.persist.rehydrate()` right after
 *   the authenticated user becomes known, so this store re-syncs to the
 *   now-correct per-user key without needing a hard page reload.
 * - Entries older than RETENTION_MS are actually pruned on every addEntry
 *   (not just filtered at display time) — an earlier version only hid old
 *   entries in NotificationPanel, which meant storage grew unbounded
 *   forever. NotificationPanel still re-filters defensively on read, for a
 *   tab that's been idle long enough that no new event has triggered a
 *   prune yet.
 */

export const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

function currentUserStorageKey(baseName: string): string {
  const user = useAuthStore.getState().user;
  const userId = user?.sub ?? user?.email ?? "anon";
  return `${baseName}-${userId}`;
}

const userScopedStorage: StateStorage = {
  getItem: (name) => localStorage.getItem(currentUserStorageKey(name)),
  setItem: (name, value) => localStorage.setItem(currentUserStorageKey(name), value),
  removeItem: (name) => localStorage.removeItem(currentUserStorageKey(name)),
};

export interface OutageNotificationEntry {
  id: string;
  facilityId: number;
  facilityName: string | undefined;
  isOngoing: boolean;
  causeAtDetection: TelemetryOutageCauseKey;
  /** Epoch ms when this notification was recorded locally. */
  timestamp: number;
  read: boolean;
}

export interface NewOutageNotification {
  facilityId: number;
  facilityName: string | undefined;
  isOngoing: boolean;
  causeAtDetection: TelemetryOutageCauseKey;
}

export interface OutageNotificationStoreState {
  entries: OutageNotificationEntry[];

  addEntry: (notification: NewOutageNotification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  clearAll: () => void;
}

function pruneStale(entries: OutageNotificationEntry[]): OutageNotificationEntry[] {
  const cutoff = Date.now() - RETENTION_MS;
  return entries.filter((entry) => entry.timestamp >= cutoff);
}

export const useOutageNotificationStore = create<OutageNotificationStoreState>()(
  persist(
    (set) => ({
      entries: [],

      addEntry: (notification) =>
        set((s) => ({
          entries: [
            {
              ...notification,
              id: crypto.randomUUID(),
              timestamp: Date.now(),
              read: false,
            },
            ...pruneStale(s.entries),
          ],
        })),

      markRead: (id) =>
        set((s) => ({
          entries: s.entries.map((entry) =>
            entry.id === id ? { ...entry, read: true } : entry,
          ),
        })),

      markAllRead: () =>
        set((s) => ({
          entries: s.entries.map((entry) => ({ ...entry, read: true })),
        })),

      clearAll: () => set({ entries: [] }),
    }),
    {
      name: "outage-notifications",
      storage: createJSONStorage(() => userScopedStorage),
      // Persist's default merge ({...currentState, ...persistedState})
      // leaves `entries` untouched when the target key has nothing to
      // merge in — fine for a normal page-load rehydrate, but wrong for
      // resyncOutageNotificationsForCurrentUser's mid-session re-rehydrate
      // (below): a brand-new user with no history of their own would
      // otherwise keep seeing whatever was in memory from the previous
      // user. Always fully trusting storage for `entries` — defaulting to
      // [] when there's nothing there — makes rehydrate() alone correct,
      // with no separate "reset first" step needed (which would itself
      // trigger a persisted write via this same middleware, clobbering the
      // new user's real stored data before it could ever be read back).
      merge: (persisted, current) => ({
        ...current,
        entries: (persisted as Partial<OutageNotificationStoreState> | null)?.entries ?? [],
      }),
    },
  ),
);

/**
 * Call right after the authenticated user becomes known (SignInForm.tsx's
 * login success, RequireAuth.tsx's boot-time token resolution) — re-syncs
 * this store to that user's own storage key.
 */
export async function resyncOutageNotificationsForCurrentUser(): Promise<void> {
  await useOutageNotificationStore.persist.rehydrate();
}
